import { Component, OnInit, Output, EventEmitter, OnDestroy, Inject, Input, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { UserApi } from '@shared-features/services/user-api.service';
import { BaseComponent } from '@shared-features/components/base-component/base.component';
import { DrawerType, Themes } from '@shared-features/enums/layout.enum';
import { LayoutService } from '../../../layout/services/layout.service';
import { DOCUMENT } from '@angular/common';
import { NotificationService } from '@features/notifications/services/notification.service';
import { ConnectedPosition } from '@angular/cdk/overlay';
import { NavBarItem } from '@shared/models/nav-bar-item.model';
import { fromEvent, merge } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { MessagingService } from '@core/services/messaging.service';
import { ConfigConstant } from '@shared/constants';
import { RoutesUtil } from '@shared-features/utils/routes.util';
import { UserType } from '@shared-features/enums';
import { BidChatService } from '@features/bid-chat/services/bid-chat.service';
import { BidChatDrawerService } from '@features/bid-chat/services/bid-chat-drawer.service';

@Component({
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HeaderComponent extends BaseComponent implements OnInit, OnDestroy {
  @Input() openMenu = false;

  @Output() openMenuChange = new EventEmitter<boolean>();
  @Output() drawerOpened = new EventEmitter();

  public unreadCount = 0;
  /** Bid chat is customer-initiated (#47189); fleet and organization users don't get the icon. */
  public isCustomer = false;
  readonly chatUnreadCount$ = this.bidChatService.unreadCount$;
  readonly isChatEnabled$ = this.bidChatDrawerService.chatEnabled$;
  public userFirstName = '';
  public userLastName = '';
  public userEmail = '';
  public userMobileNumber = '';

  /** Interpolating the parts separately would render the literal "undefined". */
  get userFullName(): string {
    return [this.userFirstName, this.userLastName].filter(Boolean).join(' ');
  }

  userTheme: Themes;
  userLogo: string;
  title: string;
  ConfigConstant = ConfigConstant;
  RoutesUtil = RoutesUtil;

  public navigationItems: NavBarItem[];
  sideMenuActive = false;
  position : ConnectedPosition = {
    originX: 'end',
    originY: 'bottom',
    overlayX: 'end',
    overlayY: 'top',
  };


  constructor(
    @Inject(DOCUMENT) private document: Document,
    public layoutService: LayoutService,
    private userApi: UserApi,
    private notificationService: NotificationService,
    private messagingService: MessagingService,
    private changeDetectorRef: ChangeDetectorRef,
    private bidChatService: BidChatService,
    private bidChatDrawerService: BidChatDrawerService
    ) {
    super();
  }

  ngOnInit(): void {
    this.layoutService.websiteDirection$
      .pipe(takeUntil(this.destroy$))
      .subscribe((dir) => {
        const isRtl = dir === 'rtl';
        this.position = {
          originX: isRtl ? 'start' : 'end',
          originY: 'bottom',
          overlayX: isRtl ? 'start' : 'end',
          overlayY: 'top',
        };
        this.changeDetectorRef.markForCheck();
      });

    this.userApi.currentUser$
      .asObservable()
      .pipe(takeUntil(this.destroy$))
      .subscribe((currentUser) => {
        const { firstName, lastName, title, whiteLabel, email, mobileNumber } = currentUser || {};
        this.isCustomer = currentUser?.roles?.[0]?.userType === UserType.CUSTOMER;
        this.userFirstName = firstName;
        this.userLastName = lastName;
        this.userEmail = email;
        this.userMobileNumber = mobileNumber;

        this.title = title;
        this.userTheme = whiteLabel?.themeName;
        this.userLogo = whiteLabel?.imageUrl;
        if (this.userTheme) {
          this.document.body.className = `scrollable scrollable--y theme theme--${this.userTheme || Themes.main}`;
        }
        this.changeDetectorRef.markForCheck();
      });

    // Initial value (null) loads the count; each FCM message and each reconnect refreshes it.
    merge(this.messagingService.currentMessage$, fromEvent(window, 'online'))
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.notificationService.refreshUnreadCount());

    this.notificationService.unreadCount$
      .pipe(takeUntil(this.destroy$))
      .subscribe((count) => {
        this.unreadCount = count;
        this.changeDetectorRef.markForCheck();
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  onChatButtonClicked(): void {
    this.bidChatDrawerService.openLogs();
  }

  onNotificationsButtonClicked() {
    this.drawerOpened.emit({
      open: true,
      type: DrawerType.Notifications,
    });
  }


  openSideMenu() {
    this.openMenuChange.emit(true)
  }

  closeSideMenu() {
    this.openMenuChange.emit(false)
  }

  handelClick(){
    if(this.openMenu){
      this.closeSideMenu()
      this.openMenuChange.emit(false)
    }else{
      this.openSideMenu()
      this.openMenuChange.emit(true)
    }
  }
}
